/* eslint-disable react/jsx-key */
import * as React from 'react';
import { observer } from 'mobx-react';

import {
    DangerAlert,
    IconTextButton,
    notification,
    SelectFormItem,
    showConfirmDialog,
} from 'ui-lib';

import { DECOMMISSION_TYPE } from '../../../../dto/device-dto';
import { useAuthStore, useDeviceStore, useNotificationsStore, useStores } from '../../../../store';

import decommissioningCss from './decommissioning.css';
import { MESSAGE_TYPE, NOTIFICATION_TYPE } from '../../../../dto/notifications-dto';
import { useLocation } from 'react-router-dom';
import { getErrorMessage } from 'common-utils';
import { ISendCommandSubmitFormValues, SendCommandForm } from '../file-transfer/send-command-form';

const DECOMMISSION_DATASOURCE = Object.values(DECOMMISSION_TYPE).map((type) => ({
    value: type,
    label: type,
}));

export const DecommissioningDevice = observer(() => {
    const { currentUser } = useAuthStore();
    const stores = useStores();
    const deviceStore = useDeviceStore();
    const notificationsStore = useNotificationsStore();
    const location = useLocation();
    const state: { uid?: string; deviceGroupName?: string } = location.state || {};

    const [selectedType, setSelectedType] = React.useState<DECOMMISSION_TYPE>(DECOMMISSION_TYPE.PARTIAL);

    const [errors, setErrors] = React.useState<string[]>([]);

    const [processing, setProcessing] = React.useState<boolean>(false);

    const submitDecommissioningDevice = async (options: ISendCommandSubmitFormValues) => {
        setErrors([]);
        setProcessing(true);
        try {
            const { varOrClientAccountId, deviceGroupName, deviceName, enableReferenceLink } = options;
            const result = await deviceStore.decommissionDevices(varOrClientAccountId, {
                deviceName: deviceName,
                deviceGroupName: deviceGroupName,
                type: selectedType,
            });
            const errors = result.devices
                .filter((device) => !device.success)
                .map((device) => `${device.identifier}: ${device.error}`);
            if (errors.length > 0) {
                setErrors(errors);
                return;
            }
            enableReferenceLink(result.devices[0].identifier);
            notification['info']({
                message: 'Decommissioned',
                description: 'The device or device group has been decommissioned successfully',
                placement: 'topRight',
            });
            await notificationsStore.create({
                content: {
                    title: 'Decommissioned',
                    text: 'The device or device group has been decommissioned successfully',
                    messageType: MESSAGE_TYPE.DEVICE,
                },
                type: NOTIFICATION_TYPE.PERSONAL,
                receiverUserUuid: currentUser.uuid,
                sender: currentUser.userName,
            });
        } catch (err) {
            setErrors([getErrorMessage(err)]);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <React.Fragment>
            {errors.length > 0 && (
                <DangerAlert
                    className={decommissioningCss['errors-alert']}
                    message='Errors'
                    description={
                        <React.Fragment>
                            {errors.map((error, index) => (
                                <p key={`error-${index}`}>{error}</p>
                            ))}
                        </React.Fragment>
                    }
                />
            )}
            <SendCommandForm
                loading={processing}
                onSubmit={async (options: ISendCommandSubmitFormValues) => {
                    if (selectedType === DECOMMISSION_TYPE.FULL) {
                        return showConfirmDialog({
                            title: 'Warning',
                            content: (
                                <React.Fragment>
                                    <p>Are you sure you want to decommission the device?</p>
                                    <p>This action:</p>
                                    <p>&#8226; removes firmware, keys, certificates from it;</p>
                                    <p>&#8226; keeps it assigned to Client account with DECOMMISSIONED status;</p>
                                    <p>&#8226; disables re-assigning it to any Client.</p>
                                    <p>Please proceed by clicking Ok or ignore the action by clicking Cancel button.</p>
                                </React.Fragment>
                            ),
                            okText: 'OK',
                            cancelText: 'Cancel',
                            onOk: () => submitDecommissioningDevice(options),
                        });
                    }
                    return showConfirmDialog({
                        title: 'Confirmation',
                        content: 'Are you sure you want to decommission this device or device group?',
                        okText: 'OK',
                        cancelText: 'Cancel',
                        onOk: () => submitDecommissioningDevice(options),
                    });
                }}
                deviceGroupName={state.deviceGroupName}
                uid={state.uid}
                extraFormItems={[
                    <SelectFormItem
                        key='decommission-select'
                        code='decommission-select'
                        label='Type'
                        labelAlign='right'
                        dataSource={DECOMMISSION_DATASOURCE}
                        selectedValue={selectedType}
                        onChange={setSelectedType}
                        colon={true}
                    />,
                    <IconTextButton
                        key='submit-decommission-button'
                        className={decommissioningCss['submit-button']}
                        type='primary'
                        htmlType='submit'
                        label='Decommission'
                        loading={processing}
                    />,
                ]}
            />
        </React.Fragment>
    );
});
